import type { Answers, BriefingExport } from "../types";

export type ImportResult =
  | { ok: true; answers: Answers; importedCompany: string; companyMismatch: boolean }
  | { ok: false; error: string };

function isBriefingExport(value: unknown, briefingId: string): value is BriefingExport {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    record.briefing === briefingId &&
    typeof record.schemaVersion === "number" &&
    typeof record.answers === "object" &&
    record.answers !== null
  );
}

/**
 * Parses a previously exported file. Does not merge — the caller decides
 * whether to merge into the current draft, after checking `companyMismatch`
 * (importing Client A's file into Client B's session is a plausible and
 * expensive mistake to make silently).
 */
export function parseBriefingImport(
  raw: string,
  currentCompany: string,
  briefingId: string,
  foreignFileError: string
): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "Die Datei ist kein gültiges JSON." };
  }

  if (!isBriefingExport(parsed, briefingId)) {
    return { ok: false, error: foreignFileError };
  }

  if (parsed.schemaVersion !== 1) {
    return { ok: false, error: `Nicht unterstützte Version (${parsed.schemaVersion}).` };
  }

  const importedCompany = (parsed.meta?.company ?? "").trim();
  const companyMismatch =
    currentCompany.trim() !== "" &&
    importedCompany !== "" &&
    currentCompany.trim().toLowerCase() !== importedCompany.toLowerCase();

  // Exports list every question with null for "unanswered" (stable schema) —
  // strip those so merging never wipes an existing answer with null.
  const answers: Answers = {};
  for (const [key, value] of Object.entries(parsed.answers)) {
    if (value !== null && value !== undefined) answers[key] = value;
  }

  return {
    ok: true,
    answers,
    importedCompany,
    companyMismatch,
  };
}
